import React, { useEffect, useRef, useState } from 'react';
import { Image, View, Text, FlatList, TouchableOpacity, Platform } from 'react-native';
import { style } from './styles';
import { ProgressBar } from './ProgressBar';
import { IMAGES } from '../../constants';
import HighlightedText from './HighlightedText';
import { getTYCrossSellData, TYCrossSellSuccessData } from './api';
import BottomSheet from './BottomSheet';
import GenericModule from '@mmt/legacy-commons/Native/GenericModule';
import LinearGradient from 'react-native-linear-gradient';
import TouchableRipple from '@mmt/legacy-commons/Common/Components/TouchableRipple';
import fecha from 'fecha';
import { trackClickEventProp61 } from '@mmt/legacy-commons/Common/utils/OmnitureTrackerUtils';
import { showCabsCrossSellTY } from '@mmt/rails/src/RailsAbConfig';
import { gradient } from '@mmt/legacy-commons/Styles/globalStyles';

declare const window: { location: { href: string } };

const isMweb = () => Platform.OS === 'web';

const openDeepLink = (deepLink: string) => {
  if (isMweb() && window) {
    window.location.href = deepLink;
  } else {
    GenericModule.openDeepLink(deepLink);
  }
};

const EVENT_PAGE_NAME = 'mob_rail_Thank_You_Success';

const handleBottomsheetRedirectComplete = (
  data: TYCrossSellSuccessData['response'],
  onClose: (closedByUser: boolean) => void
) => {
  const finalDeeplink = isMweb()
    ? data.deeplink
    : data.deeplink_data
      ? `${data.bottomsheet_deeplink}${data.deeplink_data}`
      : data.bottomsheet_deeplink;
  if (finalDeeplink) {
    trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_BS_redirect');
    openDeepLink(finalDeeplink);
    onClose(false);
  }
};

const getWidgetFinalDeeplink = (data: TYCrossSellSuccessData['response']) =>
  isMweb()
    ? data.deeplink
    : data.deeplink_data
      ? `${data.deeplink}${data.deeplink_data}`
      : data.deeplink;

const handleBottomsheetViewCabsPress = (data: TYCrossSellSuccessData['response']) => {
  const finalDeeplink = getWidgetFinalDeeplink(data);
  if (finalDeeplink) {
    trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_BS_view_cabs_click');
    openDeepLink(finalDeeplink);
  }
};

const handleWidgetCtaPress = (
  data: TYCrossSellSuccessData['response'],
  clearTimer?: () => void
) => {
  clearTimer?.();
  const finalDeeplink = getWidgetFinalDeeplink(data);
  if (finalDeeplink) {
    trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_widget_click');
    openDeepLink(finalDeeplink);
  }
};

interface TYCrossSellWidgetProps {
  fromStation: string;
  toStation: string;
  arrivalTime: string;
  onWidgetStatusFetched: ({ showBottomsheet }: { showBottomsheet: boolean }) => void;
}

const ImageCarousel = ({ imageUrls, imageDimension }: { imageUrls: string[], imageDimension?: { width: number, height: number } }) => {
  const renderItem = ({ item }: { item: string }) => (
    <Image source={{ uri: item }} style={[style.imageCard, {width: imageDimension?.width ?? 200, height: imageDimension?.height ?? 100}]} resizeMode={'cover'} />
  );

  const _renderSeperator = () => <View style={style.seperator} />;

  return (
    <View style={style.imageCarouselContainer}>
      <FlatList
        data={imageUrls}
        renderItem={renderItem}
        ListHeaderComponent={_renderSeperator}
        horizontal
        showsHorizontalScrollIndicator={false}
        decelerationRate="fast"
        getItemLayout={(_, index) => ({ length: 200, offset: index * 200, index })}
      />
    </View>
  );
};

function CrossSellInfo({
  title,
  description,
  imageUrls,
  couponText,
  persuasion,
  imageDimension,
  showCoupon,
  showPersuasion,
}: {
  title: string;
  description: string;
  imageUrls: string[];
  couponText?: string;
  persuasion?: {
    icon: string;
    text: string;
    bg_color: string;
  };
  imageDimension?: {
    width: number;
    height: number;
  };
  showCoupon: boolean;
  showPersuasion: boolean;
}) {
  return (
    <View>
      <View style={style.thankyouHeader}>
        <Text style={style.thankyouBSSubTitle}>For your transfer to/from railway station</Text>
        <Text style={style.thankyouBSTitle}>
          <HighlightedText
            str={description}
            separator="#"
            normalTxtStyle={style.thankyouBSTitle}
            highlightedTxtStyle={style.thankyouBSHighlightedTitle}
          />
        </Text>
        { couponText && showCoupon ? <Text style={style.thankyouBSCode}>{couponText}</Text> : null}
      </View>

      <View style={style.padVr16}>
        <ImageCarousel imageUrls={imageUrls} imageDimension={imageDimension} />
        { persuasion && showPersuasion ? 
        <View style={style.thankyouBSTextContainer}>
          <Image source={{uri: persuasion.icon}} style={[style.thankyouBSImage, {backgroundColor: persuasion.bg_color}]} />
          <Text style={style.thankyouBSText}>{persuasion.text}</Text>
        </View> : null}
      </View>
    </View>
  );
}

const CrossSellBottomSheet = ({
  data,
  onClose,
  visible,
}: {
  data: TYCrossSellSuccessData['response'];
  onClose: (closedByUser: boolean) => void;
  visible: boolean;
}) => {
  return (
    <BottomSheet visible={visible} onDismiss={() => onClose(true)}>
      <TouchableOpacity style={style.closeIconContainer} onPress={() => onClose(true)}>
        <Image source={{ uri: IMAGES.WHITE_CROSS }} style={style.closeIcon} />
      </TouchableOpacity>
      <View>
        <CrossSellInfo
          title={data.title}
          description={data.description}
          imageUrls={data.image_urls}
          persuasion={data.persuasion}
          imageDimension={data.images_dimension}
          couponText={data.coupon_text}
          showCoupon={data.show_coupon_bottomsheet_widget ?? false}
          showPersuasion={data.show_persuasion_bottomsheet_widget ?? false}
        />
        <ProgressBar
          duration={data.redirection_timeout}
          onComplete={() => handleBottomsheetRedirectComplete(data, onClose)}
          renderTitle={(timeLeft) =>
            data.redirection_message.replace('{time}', (timeLeft / 1000).toString())
          }
        />
        {data.bottomsheet_view_cabs_cta ? 
          <View style={style.buttonContainer}>
            <TouchableRipple onPress={() => handleBottomsheetViewCabsPress(data)}>
              <LinearGradient
                start={{x: 1.0, y: 0.0}}
                end={{x: 0.0, y: 1.0}}
                colors={[...gradient.lightBlue].reverse()}
                style={style.gradient}
              >
                <Text style={style.widgetButtonText}>{data.bottomsheet_view_cabs_cta}</Text>
              </LinearGradient>
            </TouchableRipple>
          </View> : null}
        <TouchableOpacity
          style={style.textButton}
          onPress={() => {
            trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_BS_skip');
            onClose(true);
          }}
          activeOpacity={0.8}
        >
          <Text style={style.skipCta}>{data.bottomsheet_skip_cta}</Text>
        </TouchableOpacity>
      </View>
    </BottomSheet>
  );
};

export const TYCrossSellWidget = (props: TYCrossSellWidgetProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isBottomsheetVisible, setIsBottomsheetVisible] = useState(false);
  const [data, setData] = useState<TYCrossSellSuccessData['response'] | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      const isExperimentEnabled = await showCabsCrossSellTY();

      if (!isExperimentEnabled) {
        setIsLoading(false);
        props.onWidgetStatusFetched({
          showBottomsheet: false,
        });
        return;
      }

      const date = fecha.format(new Date(props.arrivalTime), 'DD-MM-YYYY');
      const time = fecha.format(new Date(props.arrivalTime), 'HH:mm');
      const _data = await getTYCrossSellData({
        fromStation: props.fromStation,
        toStation: props.toStation,
        arrivalDate: date,
        arrivalTime: time,
        isRailTransferEnabled: false,
      });

      if (_data.status === 'SUCCESS') {
        setData(_data.response);

        if (_data.response.show_widget) {
          trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_widget');
        }

        // auto show the bottomsheet after the timeout
        if (_data.response.show_bottomsheet) {
          timerRef.current = setTimeout(() => {
            setIsBottomsheetVisible(true);
          }, _data.response.autoshow_bottomsheet_timeout);

          trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_BS');
        }

        // disable other bottomsheets in parent component if this component bottomsheet is enabled
        props.onWidgetStatusFetched({
          showBottomsheet: _data.response.show_bottomsheet,
        });
      } else {
        // API failed, allow NPS to show
        props.onWidgetStatusFetched({
          showBottomsheet: false,
        });
      }
      setIsLoading(false);
    })();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (isLoading || !data) {
    return null;
  }

  return (
    <>
      {data.show_widget ? (
        <View style={style.widgetWrapper}>
          <CrossSellInfo
            title={data.title}
            description={data.description}
            imageUrls={data.image_urls}
            couponText={data.coupon_text}
            persuasion={data.persuasion}
            imageDimension={data.images_dimension}
            showCoupon={data.show_coupon_inline_widget ?? false}
            showPersuasion={data.show_persuasion_inline_widget ?? false}
          />
          <View style={style.buttonContainer}>
            <TouchableRipple
              onPress={() =>
                handleWidgetCtaPress(data, () => {
                  if (timerRef.current) clearTimeout(timerRef.current);
                })
              }
            >
              <LinearGradient
                start={{x: 1.0, y: 0.0}}
                end={{x: 0.0, y: 1.0}}
                colors={[...gradient.lightBlue].reverse()}
                style={style.gradient}
              >
                <Text style={style.widgetButtonText}>{data.widget_cta}</Text>
              </LinearGradient>
            </TouchableRipple>
          </View>
        </View>
      ) : null}
      {data.show_bottomsheet ? (
        <CrossSellBottomSheet
          data={data}
          onClose={(closedByUser: boolean) => {
            setIsBottomsheetVisible(false);
            if (closedByUser) {
              trackClickEventProp61(EVENT_PAGE_NAME, 'rails_TY_cabs_XSell_BS_close');
            }
          }}
          visible={isBottomsheetVisible}
        />
      ) : null}
    </>
  );
};
